import type { Fatura } from '@/types/fatura'
import { formatDate } from '@/utils'
import {
    formatDetalheCategoria,
    groupDetalhesAtendimento,
} from '@/services/fatura'
import { FaturaDetalhesLista } from './FaturaDetalhesItem'
import FaturaValorResumo from './FaturaValorResumo'
import './FaturaItem.scss'

interface FaturaItemProps {
    fatura: Fatura
    className?: string
}

function getDueLabel(dataVencimento?: string) {
    if (!dataVencimento) return ''

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const due = new Date(dataVencimento.replace(/-/g, '/'))
    if (Number.isNaN(due.getTime())) return ''

    due.setHours(0, 0, 0, 0)
    const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000)

    if (diffDays < 0) return `Vencida há ${Math.abs(diffDays)} dia${Math.abs(diffDays) === 1 ? '' : 's'}`
    if (diffDays === 0) return 'Vence hoje'
    return `Vence em ${diffDays} dia${diffDays === 1 ? '' : 's'}`
}

export default function FaturaItem({ fatura, className }: FaturaItemProps) {
    const temDetalhes = fatura.Detalhes && fatura.Detalhes.length > 0
    const { grupos } = groupDetalhesAtendimento(fatura.Detalhes ?? [])
    const principal = grupos[0]?.principal
    const codigoAtendimento = grupos[0]?.index
    const dueLabel = !fatura.Pago ? getDueLabel(fatura.DataVencimento) : ''
    const overdue = dueLabel.startsWith('Vencida')
    const categoria = principal
        ? formatDetalheCategoria(
            principal,
            principal.Data ? formatDate(principal.Data) : undefined,
        )
        : ''
    const titulo =
        principal?.Tipo === 2
            ? (principal.Frequencia || principal.Descricao || fatura.Titulo)
            : (principal?.Descricao || fatura.Titulo)

    return (
        <article className={['fatura-item', className].filter(Boolean).join(' ')}>
            <div className="fatura-item-header">
                <div className="fatura-item-header-left">
                    <span className="fatura-item-numero">
                        {codigoAtendimento ? `#${codigoAtendimento}` : fatura.Titulo}
                    </span>
                    {dueLabel ? (
                        <small className={`fatura-aberta-venc-status${overdue ? ' is-overdue' : ''}`}>
                            {dueLabel}
                        </small>
                    ) : null}
                </div>
                {fatura.Pago ? <span className="fatura-item-pago">PAGO</span> : null}
            </div>

            {categoria ? <p className="fatura-item-categoria">{categoria}</p> : null}
            <h3 className="fatura-item-titulo">{titulo}</h3>

            <div className="fatura-item-info-grid">
                <div className="fatura-item-info-field">
                    <span className="fatura-item-info-label">Vencimento</span>
                    <span className="fatura-item-info-value">{formatDate(fatura.DataVencimento)}</span>
                </div>
                {fatura.DataPagamento ? (
                    <div className="fatura-item-info-field">
                        <span className="fatura-item-info-label">Pagamento</span>
                        <span className="fatura-item-info-value">{formatDate(fatura.DataPagamento)}</span>
                    </div>
                ) : null}
            </div>

            {temDetalhes ? (
                <>
                    <hr className="fatura-item-divider" aria-hidden="true" />
                    <FaturaDetalhesLista detalhes={fatura.Detalhes} hidePrincipal />
                </>
            ) : null}

            <FaturaValorResumo faturas={[fatura]} variant="compact" className="fatura-item-valores" />

            {fatura.FormaPagamento ? (
                <p className="fatura-item-forma-pagamento">Pago via {fatura.FormaPagamento}</p>
            ) : null}
        </article>
    )
}
